import { LetterheadConfig, QuoteConfig } from '../types';
import { LETTERHEAD_FIELDS, extractLetterheadConfig } from './letterheadConfig';

/**
 * profiles.settings ist EIN JSON-Objekt, in das mehrere voneinander
 * unabhaengige Oberflaechen schreiben: der globale Firmenbereich
 * (letterheadConfig, invoiceConfig, quoteConfig, footerText, senderLine,
 * constructionTypes, emailSignatureTemplate), der Angebotseditor
 * (signature), die Projektuebersicht (projectView), das Benutzerprofil
 * (mapColor) und die Rollenverwaltung (customRoles).
 *
 * Wer das GANZE Objekt aus einem im Speicher gehaltenen Stand zurueck-
 * schreibt, loescht alles, was seit dem Laden dieses Standes woanders
 * geschrieben wurde. Deshalb geht jeder Schreibvorgang ueber einen PATCH:
 * frisch lesen, flach mischen, zurueckschreiben (cloudService.
 * patchProfileSettings). Die Mischregel steht hier — ohne Datenbank, damit
 * sie geprueft werden kann.
 */
export type ProfileSettings = Record<string, any>;

/**
 * Mischt einen Patch flach in den frisch gelesenen Stand.
 *
 * Flach, nicht tief: die Bloecke (invoiceConfig, quoteConfig, …) sind je
 * genau EINER Oberflaeche zugeordnet, die sie vollstaendig verwaltet. Ein
 * tiefes Mischen wuerde dort geloeschte Felder wieder auferstehen lassen.
 *
 * Ein Patch-Wert von undefined loescht den Schluessel (JSON.stringify
 * verwirft ihn) — genau so entfernt der Angebotseditor eine Unterschrift.
 * Wer einen Schluessel nur unangetastet lassen will, legt ihn NICHT in den
 * Patch.
 */
export const mergeProfileSettings = (
    current: ProfileSettings | null | undefined,
    patch: ProfileSettings | null | undefined,
): ProfileSettings => migrateLegacyLetterhead({ ...(current || {}), ...(patch || {}) });

/**
 * Was zwischen dem frisch gelesenen und dem gemischten Stand anders ist — als
 * Auftrag an die Datenbank (Nr. 181 `profil_einstellungen_aendern`): neue oder
 * geaenderte Schluessel setzen, verschwundene entfernen. Nur diese Schluessel
 * gehen hinaus; alles andere laesst die Datenbank stehen, auch was ein
 * anderer Tab inzwischen geschrieben hat.
 *
 * Verglichen wird ueber JSON, so wie gespeichert wird: ein Patch-Wert
 * `undefined` faellt dabei weg und wird damit zu „entfernen“. Unveraenderte
 * Schluessel sind im gemischten Stand dieselben Objekte wie im gelesenen und
 * fallen deshalb nie in `setzen`.
 */
export const einstellungsAenderung = (
    vorher: unknown,
    nachher: ProfileSettings,
): { setzen: ProfileSettings; entfernen: string[] } => {
    const alt: ProfileSettings = vorher !== null && typeof vorher === 'object' && !Array.isArray(vorher)
        ? (vorher as ProfileSettings)
        : {};
    const neu: ProfileSettings = JSON.parse(JSON.stringify(nachher ?? {}));
    const setzen: ProfileSettings = {};
    for (const [schluessel, wert] of Object.entries(neu)) {
        if (JSON.stringify(alt[schluessel]) !== JSON.stringify(wert)) setzen[schluessel] = wert;
    }
    const entfernen = Object.keys(alt).filter(schluessel => !Object.prototype.hasOwnProperty.call(neu, schluessel));
    return { setzen, entfernen };
};

/** PostgREST kennt die Funktion aus Nr. 181 nicht (noch nicht gelaufen). */
export const istFehlendeEinstellungsFunktion = (e: unknown): boolean => {
    const o = (e ?? {}) as { code?: unknown; message?: unknown };
    return o.code === 'PGRST202' || o.code === '42883' || /could not find the function/i.test(String(o.message ?? ''));
};

/**
 * Zieht den Altbestand des Briefkopfs aus quoteConfig in den eigenen Block
 * und raeumt ihn dort weg.
 *
 * Vor der Umstellung lebten die neun Briefkopf-Felder in
 * settings.quoteConfig. applyLetterheadConfig laesst settings.letterheadConfig
 * blockweise darueber gewinnen, sobald der Block existiert — geht er aber
 * verloren (bis 2026-08 durch die ungeschuetzten Schreibpfade moeglich),
 * demaskiert sich der Altbestand und der Briefkopf "springt auf den alten
 * Stand zurueck". Nach dieser Migration gibt es nichts mehr, was sich
 * demaskieren koennte.
 *
 * Laeuft bei jedem Schreibvorgang mit und ist danach ein Nullvorgang: ohne
 * Altfelder in quoteConfig aendert sie nichts.
 */
export const migrateLegacyLetterhead = (settings: ProfileSettings): ProfileSettings => {
    const quoteConfig = settings.quoteConfig as QuoteConfig | undefined;
    const legacy = extractLetterheadConfig(quoteConfig);
    // Nichts zu holen: keinen leeren letterheadConfig-Block anlegen, sonst
    // saehe "nie gepflegt" aus wie "bewusst leer geraeumt".
    if (Object.keys(legacy).length === 0) return settings;

    const result: ProfileSettings = { ...settings };
    // Nur uebernehmen, wenn der neue Block fehlt. Existiert er, gilt er
    // ohnehin vollstaendig (applyLetterheadConfig) — der Altbestand wird
    // dann nur noch entfernt.
    if (result.letterheadConfig === undefined || result.letterheadConfig === null) {
        result.letterheadConfig = legacy;
    }
    result.quoteConfig = stripLegacyLetterhead(quoteConfig);
    return result;
};

/** quoteConfig ohne die neun Briefkopf-Felder. */
export const stripLegacyLetterhead = (
    quoteConfig: QuoteConfig | undefined,
): QuoteConfig | undefined => {
    if (!quoteConfig) return quoteConfig;
    const result = { ...quoteConfig } as QuoteConfig;
    for (const field of LETTERHEAD_FIELDS) {
        delete (result as LetterheadConfig)[field];
    }
    return result;
};

/**
 * Der zentrierte PDF-Fusszeilentext.
 *
 * Drei Zustaende, nicht zwei: nie gepflegt (undefined) faellt wie bisher auf
 * die Firmenanschrift zurueck, ein bewusst geleertes Feld ('') bleibt leer.
 * Mit || statt ?? faellt auch der Leerstring zurueck — die Anschrift stuende
 * sofort wieder im Feld, und der naechste Speichervorgang schriebe sie fest.
 * Genau daran liess sich die Fusszeile nicht abschalten.
 */
export const resolveFooterText = (
    footerText: string | undefined | null,
    companyAddress: string | undefined | null,
): string | undefined => (footerText ?? companyAddress) ?? undefined;
